import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

// MUI Icons
import ZoomInIcon from '@mui/icons-material/ZoomIn';

// Lightbox
import Lightbox, { SlideImage } from 'yet-another-react-lightbox';
import { Zoom } from 'yet-another-react-lightbox/plugins';
import 'yet-another-react-lightbox/styles.css';

// Custom
import useFormController from '@/components/useFormController';
import FormContent from '@/components/FormContent';
import FormButton from '@/ui/Form/FormButton';
import Loader from '@/ui/Loader/Loader';
import { GridContainer } from '@/ui/Grid';
import DataController from '@/lib/DataController';
import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';
import model from '@/models/groblja/upitnik';
import UpitnikNaslov from './UpitnikNaslov';

// Services
import { upitnikService, Upitnik, UpitnikOdgovor, UpitnikPreview, UpitnikPrivitak } from '@/services/upitnikService';

// Types
import { DCFile } from '@/@types/lib/dataController';

type UpitnikFormProps = {
  token: string;
  upitnik: Upitnik;
  onDone: (privitciOk: boolean) => void;
  onError: (message?: string) => void;
};

const PREVIEW_PX = 512;

// absolute url = external WMS, relative = our GeoServer which needs the authkey
const previewUrl = ({ url, layer, format, bbox, viewparams }: UpitnikPreview) =>
  `${url}${url.includes('?') ? '&' : '?'}SERVICE=WMS&VERSION=1.1.1&REQUEST=GetMap` +
  `&LAYERS=${encodeURIComponent(layer)}&STYLES=&SRS=EPSG:3765&BBOX=${bbox.join(',')}` +
  `&WIDTH=${PREVIEW_PX}&HEIGHT=${PREVIEW_PX}&FORMAT=${encodeURIComponent(format || 'image/jpeg')}${ 
  viewparams ? `&VIEWPARAMS=${encodeURIComponent(viewparams)}` : '' 
  }${url.indexOf('http') === 0 ? '' : `&authkey=${localStorage.getItem('auth_token')}`}`;

type ImageTileProps = {
  src: string | null; // null = still loading
  alt: string;
  aspectRatio: string;
  loaded?: boolean; // for URL sources that load in the <img> itself
  onLoad?: () => void;
  onError?: () => void;
  onClick: (src: string) => void;
};

const ImageTile = ({ src, alt, aspectRatio, loaded = true, onLoad, onError, onClick }: ImageTileProps) => {
  const ready = !!src && loaded;
  return (
    <Box sx={{ position: 'relative', width: '100%', aspectRatio }}>
      {!ready ? <Skeleton variant="rectangular" animation="wave" sx={{ position: 'absolute', inset: 0, height: '100%', borderRadius: 1 }} /> : null}
      {src ? (
        <ButtonBase
          onClick={() => onClick(src)}
          aria-label={alt}
          sx={{ cursor: 'zoom-in', display: ready ? 'block' : 'none', width: '100%', height: '100%', borderRadius: 1 }}
        >
          <Box component="img" src={src} alt={alt} onLoad={onLoad} onError={onError} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: 1 }} />
          <ZoomInIcon
            fontSize="small"
            sx={{ position: 'absolute', right: 6, bottom: 6, p: 0.25, color: 'common.white', bgcolor: 'rgba(0, 0, 0, 0.5)', borderRadius: '50%' }}
          />
        </ButtonBase>
      ) : null}
    </Box>
  );
};

const textOrNull = (value: unknown) => (typeof value === 'string' && value.trim() !== '' ? value.trim() : null);

const UpitnikForm = ({ token, upitnik, onDone, onError }: UpitnikFormProps) => {
  const { t } = useTranslation();
  const { showNotification } = useContext(SnackbarContext);

  const dc = useMemo(() => new DataController(model), []);
  const { record, validation, fields, onFieldChange, doValidate } = useFormController({
    dc,
    form: 'insert',
    mode: 'insert',
    onClose: () => {},
  });

  const [privola, setPrivola] = useState(false);
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [slika, setSlika] = useState<string | null>(null);
  const [slikaFailed, setSlikaFailed] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!upitnik.slika) return;
    upitnikService.getSlika(token).then((resp) => {
      const image = resp.success ? (resp.data as { image?: string } | undefined)?.image : undefined;
      if (image) setSlika(image);
      else setSlikaFailed(true);
    });
  }, [token, upitnik.slika]);

  const showSlika = !!upitnik.slika && !slikaFailed;
  const showPreview = !!upitnik.preview && !previewFailed;
  // side by side as squares when both exist, so the form isn't pushed far down on a phone
  const bothImages = showSlika && showPreview;

  const slides: SlideImage[] = [
    ...(slika ? [{ src: `data:image/jpeg;base64,${slika}`, alt: t('groblja:upitnik.slika') as string }] : []),
    ...(upitnik.preview && previewLoaded ? [{ src: previewUrl(upitnik.preview), alt: t('groblja:upitnik.preview') as string }] : []),
  ];

  const openLightbox = (src: string) => {
    const index = slides.findIndex((slide) => slide.src === src);
    if (index >= 0) setLightboxIndex(index);
  };

  const lokacija = [
    ['groblja:upitnik.odjel', upitnik.odjel],
    ['groblja:upitnik.polje', upitnik.polje],
    ['groblja:upitnik.red', upitnik.red],
    ['groblja:upitnik.broj', upitnik.broj],
  ].filter(([, value]) => value);

  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const handleSubmit = () => {
    setTriedSubmit(true);
    const isValid = doValidate();
    if (!isValid || !privola) {
      showNotification(t('messages.required_fields'), 'warning');
      return;
    }

    const data: UpitnikOdgovor = {
      ime: textOrNull(record.ime) as string,
      prezime: textOrNull(record.prezime) as string,
      oib: textOrNull(record.oib),
      kontakt: textOrNull(record.kontakt),
      ulica: textOrNull(record.ulica),
      mjesto: textOrNull(record.mjesto),
      ptt: (record.ptt as number) ?? null,
      drzava: textOrNull(record.drzava),
      napomena: textOrNull(record.napomena),
      privola: true,
    };
    const privitci: UpitnikPrivitak[] = ((record.privitci as DCFile[]) || []).map((f) => ({
      name: f.name,
      type: f.type,
      size: f.size,
      content: f.content as string,
    }));

    setSaving(true);
    upitnikService.insertOdgovor(token, data).then((resp) => {
      if (!resp.success) {
        setSaving(false);
        onError(resp.error?.detail?.message as string | undefined);
        return;
      }
      const { gid } = resp.data as { gid: string };
      if (privitci.length === 0) {
        onDone(true);
        return;
      }
      upitnikService.insertPrivitci(token, gid, privitci).then((pResp) => onDone(pResp.success));
    });
  };

  return (
    <Card>
      <CardContent>
        <UpitnikNaslov />
        <Typography variant="body2" gutterBottom>
          {t('groblja:upitnik.uvod')}
        </Typography>

        <Box my={2} p={2} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {showSlika || showPreview ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: bothImages ? '1fr 1fr' : '1fr', gap: 1, mb: 2 }}>
              {showSlika ? (
                <ImageTile
                  src={slika ? `data:image/jpeg;base64,${slika}` : null}
                  alt={t('groblja:upitnik.slika') as string}
                  aspectRatio={bothImages ? '1' : '3 / 2'}
                  onClick={openLightbox}
                />
              ) : null}
              {upitnik.preview && showPreview ? (
                <ImageTile
                  src={previewUrl(upitnik.preview)}
                  alt={t('groblja:upitnik.preview') as string}
                  aspectRatio="1"
                  loaded={previewLoaded}
                  onLoad={() => setPreviewLoaded(true)}
                  onError={() => setPreviewFailed(true)}
                  onClick={openLightbox}
                />
              ) : null}
            </Box>
          ) : null}
          <Typography variant="h6">{upitnik.groblje}</Typography>
          {lokacija.map(([ttoken, value]) => (
            <Typography key={ttoken} variant="body1">
              {t(ttoken as string)}: <strong>{value}</strong>
            </Typography>
          ))}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('groblja:upitnik.krivo_mjesto')}
          </Typography>
        </Box>

        <GridContainer>
          <FormContent
            fieldNames={dc.getFormFieldsNames('insert')}
            record={record}
            validation={validation}
            fields={fields}
            onFieldChange={handleFieldChange}
            mode="insert"
            columns={1}
          />
        </GridContainer>

        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" gutterBottom>
          {t('groblja:upitnik.gdpr')}
        </Typography>
        <FormControlLabel
          control={<Checkbox checked={privola} onChange={(e) => setPrivola(e.target.checked)} />}
          label={t('groblja:upitnik.privola')}
        />
        {triedSubmit && !privola ? <FormHelperText error>{t('validation.required')}</FormHelperText> : null}
      </CardContent>
      <CardActions>
        <FormButton color="primary" variant="contained" fullWidth onClick={handleSubmit} disabled={saving}>
          {t('groblja:upitnik.posalji')}
        </FormButton>
      </CardActions>
      <Loader open={saving} />
      <Lightbox
        open={lightboxIndex !== null}
        close={() => setLightboxIndex(null)}
        slides={slides}
        index={lightboxIndex ?? 0}
        carousel={{ finite: true }}
        plugins={[Zoom]}
      />
    </Card>
  );
};

export default UpitnikForm;
